import React from 'react';
import { Box, IconButton, Select, MenuItem, TextField, Typography } from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import { TemplateColumn } from '../../../../types/packager-types';

interface TemplateColumnsListProps {
  columns: TemplateColumn[];
  fields: { name: string; type: string }[];
  onUpdate: (index: number, updates: Partial<TemplateColumn>) => void;
  onRemove: (index: number) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  error?: string;
}

const TemplateColumnsList: React.FC<TemplateColumnsListProps> = ({
  columns,
  fields,
  onUpdate,
  onRemove,
  onMove,
  error,
}) => {
  if (columns.length === 0) {
    return (
      <Box
        sx={{
          mb: 2,
          p: 2,
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 2,
          backgroundColor: error ? 'error.lighter' : 'grey.50',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color={error ? 'error.main' : 'text.secondary'}>
          {error || 'No columns - click Add New to add columns'}
        </Typography>
      </Box>
    );
  }

  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (!result.destination) return;
        onMove(result.source.index, result.destination.index);
      }}
    >
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Droppable
        droppableId="template-columns-droppable"
        renderClone={(provided, snapshot, rubric) => {
          const index = rubric.source.index;
          const col = columns[index];
          return (
            <Box
              ref={provided.innerRef}
              {...provided.draggableProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                boxShadow: 4,
                zIndex: 1300,
              }}
            >
              <span
                {...provided.dragHandleProps}
                style={{
                  cursor: 'grab',
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 8,
                }}
              >
                <DragIndicatorIcon color="disabled" />
              </span>
              <Select
                size="small"
                variant="outlined"
                value={col.field}
                sx={{
                  width: 240,
                  minWidth: 240,
                  flexShrink: 0,
                  fontSize: 13,
                  height: 32,
                  minHeight: 32,
                  backgroundColor: 'background.paper',
                }}
                disabled
              >
                <MenuItem value={col.field}>{col.field}</MenuItem>
              </Select>
              <TextField
                size="small"
                variant="outlined"
                value={col.header}
                sx={{
                  flexGrow: 1,
                  minWidth: 150,
                  fontSize: 13,
                  backgroundColor: 'background.paper',
                  height: 32,
                }}
                InputProps={{ sx: { height: 32, fontSize: 13 } }}
                disabled
              />
              <IconButton size="small" disabled>
                <DeleteIcon />
              </IconButton>
            </Box>
          );
        }}
      >
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              mb: 2,
              p: 1,
              border: '1px solid',
              borderColor: error ? 'error.main' : 'divider',
              borderRadius: 2,
              backgroundColor: 'grey.50',
            }}
          >
            {columns.map((col, index) => (
              <Draggable key={`col-${col.id}`} draggableId={`col-${col.id}`} index={index}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                      p: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      boxShadow: snapshot.isDragging ? 4 : 0,
                      zIndex: snapshot.isDragging ? 1300 : 'auto',
                    }}
                  >
                    <span
                      {...provided.dragHandleProps}
                      style={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: 8,
                      }}
                    >
                      <DragIndicatorIcon color="disabled" />
                    </span>
                    <Select
                      size="small"
                      variant="outlined"
                      value={col.field}
                      onChange={(e) => onUpdate(index, { field: e.target.value, header: e.target.value })}
                      sx={{
                        width: 240,
                        minWidth: 240,
                        flexShrink: 0,
                        fontSize: 13,
                        height: 32,
                        minHeight: 32,
                        backgroundColor: 'background.paper',
                      }}
                    >
                      {fields.map((f) => (
                        <MenuItem key={f.name} value={f.name}>
                          {f.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Header"
                      value={col.header}
                      onChange={(e) => onUpdate(index, { header: e.target.value })}
                      sx={{
                        flexGrow: 1,
                        minWidth: 150,
                        fontSize: 13,
                        backgroundColor: 'background.paper',
                        height: 32,
                      }}
                      InputProps={{
                        sx: {
                          height: 32,
                          minHeight: 32,
                          fontSize: 13,
                        },
                      }}
                      inputProps={{
                        style: {
                          height: 32,
                          fontSize: 13,
                          padding: '0 8px',
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => onRemove(index)}
                      disabled={columns.length === 1}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default TemplateColumnsList;
